import { Input, message, Spin } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { requestCertificate } from '../../../../api/aopsHR.api'
import AopsCommonModalLayout from '../../../../components/apos/modal/AopsCommonModalLayout'
import '../orgChart.scss'
import CertificateRecipientInfo from '../ui/CertificateRecipientInfo'
import { generateEmploymentCertificatePdf } from '../utils/generateEmploymentCertificatePdf'

// 재직증명서 발급 모달
const EmploymentCertificateModal = ({
  open,
  onClose,
  personalInfo,
  employmentInfo,
  employeeId,
  userId,
  onRequest,
}) => {
  const [selectedPurpose, setSelectedPurpose] = useState('')
  const [customPurpose, setCustomPurpose] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)

  const purposeOptions = ['금융기관 제출', '관공서 제출']

  // 목적 타입을 enum 값으로 매핑
  const getPurposeType = (purpose) => {
    if (purpose === '금융기관 제출') return 'FINANCIAL'
    if (purpose === '관공서 제출') return 'GOVERNMENT'
    if (purpose === '기타') return 'OTHER'
    return 'EXTERNAL_ORG'
  }

  // 발급하기 버튼 disabled 조건
  const isSubmitDisabled =
    !selectedPurpose ||
    (selectedPurpose === '기타' && !customPurpose.trim()) ||
    isGenerating

  const handleRequest = async () => {
    if (!employeeId || !userId) {
      message.error('직원 정보가 없습니다.')
      return
    }

    const purpose = selectedPurpose === '기타' ? customPurpose : selectedPurpose
    const purposeType = getPurposeType(selectedPurpose)

    setIsGenerating(true)

    try {
      // 1. 서버에 증명서 발급 이력 저장 (POST 요청)
      const res = await requestCertificate(employeeId, purposeType, userId)
      console.log('requestCertificate response::', res)
      const certificateNo = res?.data?.certificateNo

      // 2. PDF 생성 및 다운로드
      const pdfData = {
        employeeName: personalInfo?.name,
        birthDate: personalInfo?.birthday || personalInfo?.birthDate,
        address: personalInfo?.address,
        employeeType: employmentInfo?.rank || employmentInfo?.title,
        employmentStartDate:
          employmentInfo?.joinDate || employmentInfo?.startDate,
        jobGroup: employmentInfo?.position || employmentInfo?.jobGroup,
        department: employmentInfo?.teamName,
        purpose: purpose,
        issueDate: new Date(),
        certificateNo: certificateNo,
      }

      // PDF 생성
      const pdfBlob = await generateEmploymentCertificatePdf(pdfData)

      // PDF 다운로드
      const url = window.URL.createObjectURL(pdfBlob)
      const link = document.createElement('a')
      link.href = url
      link.download = `재직증명서_${personalInfo?.name || '직원'}_${dayjs().format('YYYYMMDD')}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      message.success(
        '재직증명서가 발급되었습니다. PDF 파일이 다운로드되었습니다.',
      )
      onClose()
      onRequest?.()
    } catch (error) {
      console.error('재직증명서 발급 오류:', error)
      message.error(
        error.response?.data?.message ||
          '재직증명서 발급 중 오류가 발생했습니다. 다시 시도해주세요.',
      )
    } finally {
      setIsGenerating(false)
    }
  }

  useEffect(() => {
    if (open) {
      setSelectedPurpose('')
      setCustomPurpose('')
    }
  }, [open])

  return (
    <AopsCommonModalLayout
      open={open}
      onClose={onClose}
      title="재직증명서 발급"
    >
      <Spin spinning={isGenerating} tip="PDF 생성 중...">
        <div className="form-container">
          <div className="form-wrapper">
            {/* 발급자 정보 */}
            <CertificateRecipientInfo
              description={dayjs().format('YY년 MM월 DD일')}
              description2={
                <span className="certificate-issue-name">
                  {employmentInfo?.teamName}팀 · {personalInfo?.name}
                </span>
              }
            />

            {/* 전자문서 형태 */}
            <div
              className="certificate-document-type-container"
              style={{ alignItems: 'flex-start', gap: '20px' }}
            >
              <h3
                className="certificate-document-type-title"
                style={{ alignItems: 'flex-start' }}
              >
                전자문서 형태
              </h3>
              <div className="status-button-group">
                <div
                  style={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    gap: '12px',
                  }}
                >
                  <button
                    type="button"
                    className="status-tag active"
                    disabled
                    style={{ marginBottom: '12px', alignSelf: 'flex-start' }}
                  >
                    재직증명서
                  </button>

                  {/* 발급 용도 */}
                  <div className="certificate-purpose-section">
                    <h3 className="certificate-purpose-section-title">
                      발급 용도를 선택해주세요
                    </h3>
                    <div className="certificate-purpose-buttons-container">
                      <div
                        className="status-button-group"
                        style={{ paddingLeft: '10px' }}
                      >
                        {purposeOptions.map((purpose) => (
                          <button
                            key={purpose}
                            type="button"
                            className={`status-tag ${
                              selectedPurpose === purpose ? 'active' : ''
                            }`}
                            onClick={() => {
                              setSelectedPurpose(purpose)
                              setCustomPurpose('')
                            }}
                          >
                            {purpose}
                          </button>
                        ))}
                        <button
                          type="button"
                          className={`status-tag ${
                            selectedPurpose === '기타' ? 'active' : ''
                          }`}
                          onClick={() => setSelectedPurpose('기타')}
                        >
                          기타 직접입력
                        </button>
                      </div>
                      {selectedPurpose === '기타' && (
                        <div
                          className="certificate-custom-purpose-wrapper"
                          style={{ paddingTop: '8px' }}
                        >
                          <Input
                            placeholder="기타 직접입력 : 제출 기관을 적어주세요"
                            value={customPurpose}
                            onChange={(e) => setCustomPurpose(e.target.value)}
                            onBlur={() => {
                              if (!customPurpose.trim()) {
                                setSelectedPurpose('')
                              }
                            }}
                            className="certificate-custom-purpose-input"
                            autoFocus
                            style={{ height: '48px' }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 하단 버튼 */}
            <div className="popup-footer">
              <button className="btn-cancel" onClick={onClose}>
                취소
              </button>
              <button
                className="btn-submit"
                onClick={handleRequest}
                disabled={isSubmitDisabled}
              >
                발급하기
              </button>
            </div>
          </div>
        </div>
      </Spin>
    </AopsCommonModalLayout>
  )
}

export default EmploymentCertificateModal
